<template>
    <div class="spark-sequence-container">
        <div class="spark-sequence-header">
            <h5 class="spark-sequence-header__title">
                <i class="fa-solid fa-arrow-down-1-9 text-vertex-blue"></i>
                <span>Section Sequence</span>
            </h5>
            <button class="spark-btn-header spark-btn-header--primary"
                @click="saveSequence" :disabled="isSaving || !draggableSections.length">
                <i class="fa-solid fa-floppy-disk me-1"></i> {{ isSaving ? 'Saving...' : 'Save Sequence' }}
            </button>
        </div>
        <p class="spark-sequence-hint">
            Drag sections to reorder. Toggle the eye icon to show or hide a section from the client.
        </p>

        <!-- Draggable sections (everything except Schedule & Execution) -->
        <draggable v-model="draggableSections" group="sections" item-key="type"
            handle=".spark-sequence-handle"
            @end="onReorderEnd" animation="200"
            class="spark-sequence-list mb-2">
            <template #item="{ element, index }">
                <div class="spark-sequence-item"
                    :class="{ 'spark-sequence-item--hidden': element.is_hidden }">

                    <span class="spark-sequence-handle" title="Drag to reorder">
                        <i class="fa-solid fa-grip-vertical"></i>
                    </span>

                    <span class="spark-sequence-num">{{ index + 1 }}</span>

                    <span class="spark-sequence-name">
                        {{ formatSectionName(element.type) }}
                    </span>

                    <span v-if="element.is_hidden" class="spark-sequence-badge">Hidden</span>

                    <button type="button" class="spark-sequence-visibility-btn"
                        :class="{ 'spark-sequence-visibility-btn--hidden': element.is_hidden }"
                        :title="element.is_hidden ? 'Section is hidden — click to show' : 'Section is visible — click to hide'"
                        @click.stop="toggleDraggableVisibility(index)">
                        <i class="fa-solid" :class="element.is_hidden ? 'fa-eye-slash' : 'fa-eye'"></i>
                    </button>
                </div>
            </template>
        </draggable>

        <!-- Locked sections (Schedule & Execution) — always pinned at the bottom, always visible -->
        <div class="spark-sequence-list">
            <div v-for="(element, lIdx) in lockedSections" :key="element.type"
                class="spark-sequence-item spark-sequence-item--locked">

                <span class="spark-sequence-handle text-muted" style="cursor: not-allowed;" title="This section is locked in position">
                    <i class="fa-solid fa-lock"></i>
                </span>

                <span class="spark-sequence-num">{{ draggableSections.length + lIdx + 1 }}</span>

                <span class="spark-sequence-name">
                    {{ formatSectionName(element.type) }}
                </span>

                <span class="spark-sequence-badge">Locked</span>
                
                <span class="spark-sequence-visibility-btn text-muted" style="cursor: default;" title="Always visible">
                    <i class="fa-solid fa-eye"></i>
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import draggable from 'vuedraggable';

const LOCKED_SECTION_TYPES = ['ScheduleTable', 'ExecutionPage'];

export default {
    name: 'ContractSequence',
    components: { draggable },
    props: {
        modelValue: { type: Array, default: () => [] }
    },
    emits: ['update:modelValue', 'save'],
    data() {
        return {
            isSaving: false,
            draggableSections: [],
            lockedSections: []
        };
    },
    watch: {
        modelValue: {
            handler(newVal) {
                if (newVal && newVal.length > 0) {
                    const all = JSON.parse(JSON.stringify(newVal));
                    this.draggableSections = all.filter(s => !LOCKED_SECTION_TYPES.includes(s.type));
                    // Preserve the fixed order: ScheduleTable first, then ExecutionPage
                    this.lockedSections = LOCKED_SECTION_TYPES
                        .map(type => all.find(s => s.type === type))
                        .filter(Boolean);
                    // Ensure locked sections are never hidden
                    this.lockedSections.forEach(s => { s.is_hidden = false; });
                }
            },
            immediate: true
        }
    },
    methods: {
        formatSectionName(type) {
            const names = {
                MasterTerms: 'Master Terms',
                ServiceModuleStandard: 'Service Module — Standard',
                ServiceModuleTerm: 'Service Module — Term',
                ServiceModuleSubscription: 'Service Module — Subscription',
                ScheduleTable: 'Schedule',
                ExecutionPage: 'Execution Page',
            };
            return names[type] || type;
        },

        toggleDraggableVisibility(index) {
            this.draggableSections[index].is_hidden = !this.draggableSections[index].is_hidden;
        },

        onReorderEnd() {
            // Reassign sequence numbers: draggable first, locked always last
            const combined = [...this.draggableSections, ...this.lockedSections];
            combined.forEach((s, i) => { s.sequence = i + 1; });
        },

        async saveSequence() {
            this.isSaving = true;
            try {
                this.onReorderEnd();
                // Force locked sections to always be visible
                this.lockedSections.forEach(s => { s.is_hidden = false; });
                const combined = [...this.draggableSections, ...this.lockedSections];
                this.$emit('update:modelValue', JSON.parse(JSON.stringify(combined)));
                this.$emit('save');
            } catch (e) {
                this.$toast.error('Failed to save sequence.');
            } finally {
                this.isSaving = false;
            }
        }
    }
}
</script>

<style scoped>
.draggable-row {
    cursor: grab;
}
.draggable-row:active {
    cursor: grabbing !important;
}
.transition-all {
    transition: all 0.2s ease;
}
.text-vertex-blue {
    color: #00A0C1;
}
</style>
